// client/src/pages/seo/QrCodeMenuWithoutAppNo.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "answer", label: "Hvordan fungerer en QR-meny uten app?" },
  { id: "scanner", label: "Trenger gjestene en egen QR-skanner?" },
  { id: "browser", label: "Nettlesermeny eller restaurantapp?" },
  { id: "requirements", label: "Hva bør en god nettleserbasert meny kunne gjøre?" },
  { id: "mistakes", label: "Vanlige feil" },
  { id: "faq", label: "Vanlige spørsmål" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuWithoutAppNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title = "QR-kode-meny uten app: åpne menyen i nettleseren | Best Place";
    setMeta(
      "description",
      "QR-kode-meny uten app: gjestene skanner koden med mobilen og åpner menyen direkte i nettleseren – uten nedlasting, innlogging eller registrering."
    );
    setCanonical("https://best-place.club/no/qr-kode-meny-uten-app");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/qr-code-speisekarte-ohne-app",
          en: "/en/qr-code-menu-without-app",
          no: "/no/qr-kode-meny-uten-app",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR-kode-meny uten app</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Åpne kameraet, skann QR-koden, trykk på lenken og se menyen i nettleseren. Gjestene bør ikke måtte gjøre mer enn dette bare for å åpne en meny.
              </p>
              <p className="seoHero__text">
                Denne siden dekker bevisst bare ett spørsmål: hvordan en QR-meny fungerer uten app. For valg, opprettelse og bruk av en komplett QR-meny, se hovedsiden vår.
              </p>
              <p>
                <Link to="/no/qr-kode-meny-for-restauranter">Gå til QR-kode-menyer for restauranter</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Innhold</h2>
        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>Hvordan fungerer en QR-meny uten app?</h2>
          <p>
            QR-koden inneholder eller peker til en vanlig nettlenke. Moderne smarttelefoner gjenkjenner lenken etter skanning og tilbyr å åpne den direkte. Menyen lastes deretter som en mobil nettside i nettleseren.
          </p>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Åpne kameraet på mobilen.</li>
            <li className="text__seo-li">Skann QR-koden på bordet.</li>
            <li className="text__seo-li">Trykk på lenken som vises.</li>
            <li className="text__seo-li">Se menyen direkte i nettleseren.</li>
          </ol>
          <p>
            Gjestene trenger verken App Store, Google Play, konto eller innlogging.
          </p>
        </section>

        <section id="scanner" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger gjestene en egen QR-skanner?</h2>
            <p>
              Som regel ikke. På moderne iPhone- og Android-enheter kan det innebygde kameraet gjenkjenne QR-koder. Viktigere enn en egen skannerapp er en tydelig trykt kode og en tilgjengelig målside.
            </p>
          </div>
        </section>

        <section id="browser" className="acquaintance">
          <h2>Nettlesermeny eller restaurantapp?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Nettlesermeny</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">tilgjengelig med en gang</li>
                <li className="text__seo-li">ingen nedlasting</li>
                <li className="text__seo-li">bra for førstegangsbesøkende</li>
                <li className="text__seo-li">enkel tilgang ved bordet</li>
              </ul>
            </div>
            <div className="seoComparison__col">
              <h3>Egen app</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">kan være nyttig for faste kunder</li>
                <li className="text__seo-li">nyttig for lojalitet eller komplekse funksjoner</li>
                <li className="text__seo-li">krever ekstra installasjon</li>
                <li className="text__seo-li">ofte unødvendig bare for å se menyen</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="requirements" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør en god nettleserbasert meny kunne gjøre?</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">laste raskt på mobilen</li>
              <li className="text__seo-li">vise tydelige kategorier</li>
              <li className="text__seo-li">vise priser tydelig uten zooming</li>
              <li className="text__seo-li">bruke bilder bare der de hjelper</li>
              <li className="text__seo-li">støtte flere språk ved behov</li>
              <li className="text__seo-li">åpnes uten innlogging eller registrering</li>
            </ul>
            <p>
              The browser question is only one part of the product decision. If you
              are looking for a complete solution, read{" "}
              <Link to="/no/qr-kode-meny-for-restauranter">QR-kode-meny for restauranter</Link>.
            </p>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Vanlige feil med QR-menyer uten app</h2>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">QR-koden åpner bare en PDF-fil som er vanskelig å lese.</li>
            <li className="text__seo-li">Mobilsiden laster sakte eller bruker for liten tekst.</li>
            <li className="text__seo-li">Det kreves fortsatt registrering etter skanning.</li>
            <li className="text__seo-li">QR-koden ble ikke testet på flere enheter før trykking.</li>
            <li className="text__seo-li">Det står ingen forklaring ved koden om hva som åpnes etter skanning.</li>
          </ul>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige spørsmål om QR-kode-meny uten app</h2>
            <div className="seoFaq">
              <details>
                <summary>Fungerer det på iPhone?</summary>
                <p>Ja. Kameraet kan gjenkjenne QR-koden, og lenken kan åpnes i nettleseren.</p>
              </details>
              <details>
                <summary>Fungerer det på Android?</summary>
                <p>Ja. Moderne Android-enheter gjenkjenner vanligvis QR-koder via kameraet eller Google Lens.</p>
              </details>
              <details>
                <summary>Kan en PDF også åpnes uten app?</summary>
                <p>Ja, men en PDF er ikke automatisk mobilvennlig. En ekte mobil menyside er ofte lettere å lese.</p>
              </details>
              <details>
                <summary>Må jeg utvikle en egen restaurantapp?</summary>
                <p>Vanligvis ikke hvis formålet bare er å vise menyen ved bordet.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Relaterte temaer</h2>
          <div className="seoCompareSmall">
            <p><Link to="/no/qr-kode-meny-for-restauranter">QR-kode-meny for restauranter</Link></p>
            <p><Link to="/no/qr-kode-meny-utskrift">Trykk en QR-kode</Link></p>
            <p><Link to="/no/digital-meny-for-restauranter">Digital meny</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
